import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { User, Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-5 py-10"
      style={{
        background:
          "linear-gradient(135deg, #f8fbff 0%, #f4f8fd 45%, #eef5fb 100%)",
      }}
      data-testid="login-page"
    >
      <div
        className="w-full max-w-[460px] rounded-[24px] bg-white px-8 py-9 sm:px-9 sm:py-10"
        style={{
          boxShadow:
            "0 30px 70px rgba(15, 23, 42, 0.14), 0 1px 0 rgba(255,255,255,0.9) inset",
          border: "1px solid rgba(226, 232, 240, 0.95)",
        }}
      >
        <div className="text-center mb-9">
          <div
            className="font-heading font-black tracking-tight leading-none"
            style={{
              fontSize: "42px",
              color: "#2f6eea",
              letterSpacing: "-2px",
              textTransform: "lowercase",
            }}
          >
            camaroo
          </div>

          <h1
            className="font-heading mt-7 text-[28px] font-bold tracking-tight"
            style={{ color: "#101828" }}
          >
            Welcome Back
          </h1>

          <p className="mt-2 text-[14px]" style={{ color: "#7b8798" }}>
            Enter your credentials to access your account
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
          <div>
            <label
              className="block text-[14px] font-medium mb-2"
              style={{ color: "#344054" }}
            >
              User Name
            </label>

            <div className="relative">
              <User
                size={19}
                strokeWidth={1.8}
                className="absolute left-5 top-1/2 -translate-y-1/2"
                style={{ color: "#98a2b3" }}
              />
              <input
                className="w-full h-[48px] rounded-[13px] border pl-12 pr-4 text-[14px] outline-none transition"
                style={{
                  background: "#eaf2ff",
                  borderColor: "#cbd5e1",
                  color: "#101828",
                }}
                type="text"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>
          </div>

          <div>
            <label
              className="block text-[14px] font-medium mb-2"
              style={{ color: "#344054" }}
            >
              Password
            </label>

            <div className="relative">
              <Lock
                size={19}
                strokeWidth={1.8}
                className="absolute left-5 top-1/2 -translate-y-1/2"
                style={{ color: "#98a2b3" }}
              />

              <input
                className="w-full h-[48px] rounded-[13px] border pl-12 pr-12 text-[14px] outline-none transition"
                style={{
                  background: "#eaf2ff",
                  borderColor: "#cbd5e1",
                  color: "#101828",
                }}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
              />

              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-5 top-1/2 -translate-y-1/2"
                style={{ color: "#98a2b3" }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff size={19} strokeWidth={1.8} />
                ) : (
                  <Eye size={19} strokeWidth={1.8} />
                )}
              </button>
            </div>
          </div>

          {err && (
            <div
              className="text-[13px] px-4 py-3 rounded-[12px]"
              style={{
                background: "#fef2f2",
                color: "#991b1b",
                border: "1px solid #fecaca",
              }}
              data-testid="login-error"
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-[50px] rounded-[13px] text-white text-[15px] font-medium transition disabled:opacity-70"
            style={{
              background:
                "linear-gradient(90deg, #3b82f6 0%, #4f46e5 100%)",
              boxShadow: "0 12px 28px rgba(59, 130, 246, 0.25)",
            }}
            data-testid="login-submit"
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
